// перегруз стандартных прототипов THREE.JS
import './bvhInit.js';

import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import FileHelper from './fileHelper.js';
import MathHelper from './mathHelper.js';


// singleton class
export default class AssetRepository {

	static instance = null;

	#gltfLoader;
	#assetMap = new Map();
	#assetObjectsMap = new Map();
	
	constructor() {
		if (AssetRepository.instance) {
			return AssetRepository.instance;
		}

		AssetRepository.instance = this;
		const manager = new THREE.LoadingManager();
		this.#gltfLoader = new GLTFLoader(manager).setPath('assets/models/');
	}

	async #getAsset(assetName) {
		const prefab = this.#assetMap.get(assetName);
		if (!prefab || !prefab.Lods || prefab.Lods.length === 0) {
			return null;
		}

		const lodGroup = new THREE.LOD();
		lodGroup.name = assetName;

		const maxDistance = prefab?.MaxDistance || 10000;

		const lodResults = await Promise.all(prefab.Lods.map(async (lod, i) => {
			const modelPath = lod.ModelPath;
			if (!modelPath)
				return;

			const loader = this.#gltfLoader;

			try {
				const loadedContent = await loader.loadAsync(modelPath);

				const model = (loadedContent.scene || loadedContent).clone(true);

				const distance = lod.Distance ?? 0;

				let lodMaxDistance = prefab.Lods[i + 1]?.Distance ?? maxDistance;

				model.userData.minDistance = distance;
				model.userData.maxDistance = lodMaxDistance;
				model.userData.assetInfo = prefab.NgnInfo || {};

				const rotation = new THREE.Euler(
					MathHelper.degreesToRadians(lod.DefaultRotation?.[0] ?? 0),
					MathHelper.degreesToRadians(lod.DefaultRotation?.[1] ?? 0),
					MathHelper.degreesToRadians(lod.DefaultRotation?.[2] ?? 0),
					'XYZ'
				);
				
				const scale = new THREE.Vector3(
					lod.DefaultScale?.[0] ?? 1,
					lod.DefaultScale?.[1] ?? 1,
					lod.DefaultScale?.[2] ?? 1
				);

				const targetMatrix = new THREE.Matrix4();
				targetMatrix.compose(new THREE.Vector3(0, 0, 0), new THREE.Quaternion().setFromEuler(rotation), scale);


				// применяем transforms (пока без DefaultPosition) из конфигурационного файла ассета, которые переопределяют матрицу меша
				model.traverse((node) => {
					if (!node.isMesh)
						return;

					node.castShadow = true;
					node.receiveShadow = true;
					node.geometry.computeBoundsTree();

					const finalMatrix = new THREE.Matrix4();
					finalMatrix.multiplyMatrices(targetMatrix, node.matrix);

					node.geometry.applyMatrix4(finalMatrix);

					node.position.set(0, 0, 0);
					node.rotation.set(0, 0, 0);
					node.scale.set(1, 1, 1);

					node.matrix.identity();
					node.matrixWorld.identity();
					node.matrixAutoUpdate = false;
				});

				// Обнуляем рутовую матрицу группу
				model.position.set(0, 0, 0);
				model.rotation.set(0, 0, 0);
				model.scale.set(1, 1, 1);
				model.updateMatrix();

				const paramsContainer = {
					obj: model,
					materials: lod.Materials,
					sliceGeometry: !!lod?.sliceGeometry,
					polygonPercent: Number.parseInt(lod.AutoSimplifyMeshPercent) || 100
				}

				return { model, distance };
			} catch (error) {
				console.error(`AssetRepository: Failed to load LOD ${i} for ${assetName}:`, error);
			}
		}));

		lodResults
			.filter(res => !!res)
			.sort((obj1, obj2) => obj1.distance - obj2.distance)
			.forEach(res => lodGroup.addLevel(res.model, res.distance));

		return lodGroup;
	}

	getAssetInfo(assetName) {
		return this.#assetMap.get(assetName);
	}

	async loadAssetsFromAssetListFile(fileName) {

		await this.dispose();
		try {
			const assetListData = await FileHelper.readServerFileAsync(fileName);
			console.log(fileName);
			const fileArr = JSON.parse(assetListData);

			if (!Array.isArray(fileArr)) {
				throw new Error("Asset list must be an array");
			}

			const prefabPromises = fileArr.map(async (filePart) => {
				console.log(`load asset ${filePart}`);
				const prefabFileName = `/assets/json/${filePart}.json`;
				const content = await FileHelper.readServerFileAsync(prefabFileName);
				const prefab = JSON.parse(content);
				return this.loadAsset(prefab);
			});

			await Promise.all(prefabPromises);
		} catch (error) {
			console.error(`AssetRepository: Error loading asset list: ${error.message}`);
			throw error;
		}
	}

	async loadAsset(prefab) {
		if (!prefab?.AssetName) {
			console.error("AssetRepository: Prefab data is incorrect or missing AssetName");
			return;
		}

		this.#assetMap.set(prefab.AssetName, prefab);

		const assetPromise = this.#getAsset(prefab.AssetName);
		this.#assetObjectsMap.set(prefab.AssetName, assetPromise);

		return assetPromise;
	}

	async createFromAsset(assetName) {
		const assetPromise = this.#assetObjectsMap.get(assetName);
		if (!assetPromise) {
			throw new Error(`AssetRepository: No asset registered with name ${assetName}`);
		}

		const result = await assetPromise;
		const newObject = result.clone();

		newObject.userData.ngnInfo = result.userData.ngnInfo
			? JSON.parse(JSON.stringify(result.userData.ngnInfo))
			: {};

		if (result instanceof THREE.LOD) {
			for (let i = 0; i < result.levels.length; i++) {
				const originalLod = result.levels[i].object;
				const clonedLod = newObject.levels[i].object;
				clonedLod.userData = originalLod.userData;

				clonedLod.traverse(clonedChildObject => {
					if (!(clonedChildObject instanceof THREE.Object3D)) {
						return;
					}
					clonedChildObject.userData = clonedLod.userData;
				});
			}
		}

		newObject.userData.inObjectType = "asset";
		return newObject;
	}

	async dispose() {
		for (const [name, promise] of this.#assetObjectsMap) {
			try {
				const group = await promise;
				if (!group)
					continue;

				group.traverse((child) => {
					if (child.geometry) {
						child.geometry.dispose();
					}
					if (child.material) {
						const materials = Array.isArray(child.material) ? child.material : [child.material];
						materials.forEach(m => {
							if (typeof m.dispose === 'function') {
								m.dispose();
							}
						});
					}
				});
			} catch (error) {
				console.error(`AssetRepository: Error disposing asset ${name}:`, error);
			}
		}

		this.#assetMap.clear();
		this.#assetObjectsMap.clear();
	}

}